import { t } from '../../strings';
import { el, ensureStyle } from '../dom';
import { SKILLS, type Skill } from '../../types';
import {
  dueLabel,
  standingWord,
  type SpeciesStanding,
  type Standing,
} from '../../core/progress';

// Three dots — sound, names, photo — that say where one bird stands in each
// skill. Shared, because the Library's rows, the Library's filter chips and the
// species page all have to be visibly the same four states; a row of dots that
// meant something slightly different from the chip above it would be worse than
// no dots at all.
//
// The states are told apart by fill and by ring, not by hue: the hue is already
// spoken for by the skill (the same three colours as the mastery rings and the
// study grid), so it cannot also carry the standing.

ensureStyle(
  'mastery-dots-css',
  `
  /* The ring on a due dot is drawn OUTSIDE the 9px box, so the row reserves the
     space whether or not anything is due — otherwise a bird falling due would
     nudge its neighbours. */
  .mdots { display:flex; align-items:center; gap:7px; padding:3px; }
  .mdot { width:9px; height:9px; border-radius:50%; flex:none; box-sizing:border-box;
    --dot-gap:var(--paper); }
  .mdot.solid { background:currentColor; }
  /* Filled like solid, then haloed: due reads as "flagged", not as "less". */
  .mdot.due { background:currentColor;
    box-shadow:0 0 0 1.5px var(--dot-gap), 0 0 0 3px currentColor; }
  /* Met but not solid — the same colour, faded to the edge of legibility, which
     is roughly what the memory is doing. */
  .mdot.weak { background:currentColor; opacity:0.4; }
  .mdot.unmet { background:none; border:1.5px solid var(--hairline-strong); }
`,
);

/** Each skill's name, as every screen writes it. */
export const SKILL_LABEL: Record<Skill, string> = {
  sound: t('common.skill.sound'),
  names: t('common.skill.names'),
  photo: t('common.skill.photo'),
};

/** The mastery-ring hues, so a dot and its ring are recognisably one skill. */
export const SKILL_COLOR: Record<Skill, string> = {
  sound: 'var(--skill-sound)',
  names: 'var(--skill-names)',
  photo: 'var(--skill-photo)',
};

export interface DotOpts {
  /**
   * Skill hue. Left off by the legend dots on the Library's filter chips, which
   * inherit the chip's own ink instead — those say which STANDING the chip
   * selects, and a skill colour on them would claim a skill they don't mean.
   */
  color?: string;
  /** Spoken and hovered reading — "Sound ID: due now". */
  label?: string;
}

/**
 * One dot. `currentColor` carries the skill hue so a single rule set covers
 * fill, halo and border across all three skills and both themes.
 */
export function masteryDot(standing: Standing, opts: DotOpts = {}): HTMLElement {
  const node = el(
    `span.mdot.${standing}`,
    opts.color ? { style: { color: opts.color } } : {},
  );
  if (opts.label) {
    node.setAttribute('role', 'img');
    node.setAttribute('aria-label', opts.label);
    node.title = opts.label;
  }
  return node;
}

/** A bird's three dots, in the app's fixed skill order. */
export function masteryDots(sp: SpeciesStanding, now: Date = new Date()): HTMLElement {
  return el(
    'span.mdots',
    { role: 'group', 'aria-label': t('common.yourStanding') },
    ...SKILLS.map((skill) => {
      const s = sp.bySkill[skill];
      // Both facts on the dot: what it is, and when the app comes back for it.
      // "Sound ID: shaky · due in 3 days" is the whole row's worth of detail in
      // a tooltip, which is what keeps the list itself down to three dots.
      const when = dueLabel(s.due, now);
      const standing = standingWord(s.standing);
      const reading =
        s.standing === 'unmet' || when === standing
          ? t('common.skillStanding', { skill: SKILL_LABEL[skill], standing })
          : t('common.skillStandingDue', { skill: SKILL_LABEL[skill], standing, when });
      return masteryDot(s.standing, { color: SKILL_COLOR[skill], label: reading });
    }),
  );
}
